import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HeartPulse, Save } from "lucide-react";
import api from "../../lib/api";

/* Emergency Travel Card — encrypted fields with per-group disclosure rules. */
const GROUPS = [
  ["identity", "Identity", [["full_name", "Full legal name"], ["nationality", "Nationality"], ["preferred_language", "Preferred language"], ["date_of_birth", "Date of birth (optional)"]]],
  ["medical", "Medical", [["allergies", "Critical allergies"], ["conditions", "Critical medical conditions"], ["medicines", "Required medicines"], ["physician", "Physician contact"]]],
  ["insurance", "Travel Insurance", [["provider", "Insurance provider"], ["assistance_number", "Assistance number"], ["policy_ref", "Policy reference"]]],
  ["contacts", "Contacts & Embassy", [["emergency_contacts", "Emergency contacts (name + phone)"], ["embassy", "Embassy / consulate info"]]],
  ["trip", "Current Trip", [["hotel", "Hotel"], ["tour_operator", "Tour operator"], ["transport_info", "Flight / train / cruise info"]]],
];
const DISCLOSURES = [
  ["during_trip", "While I'm on a trip"],
  ["escalation_only", "Only after a missed check-in"],
  ["sos_only", "Only during an SOS"],
  ["never", "Never (only me)"],
];

export default function GuardianEmergencyCard() {
  const [fields, setFields] = useState({});
  const [rules, setRules] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/guardian/emergency-card").then((r) => {
      setFields(r.data.fields || {}); setRules(r.data.disclosure_rules || {}); setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const setF = (g, k, v) => setFields((f) => ({ ...f, [g]: { ...(f[g] || {}), [k]: v } }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/guardian/emergency-card", { fields, disclosure_rules: rules }); toast.success("Emergency card saved (encrypted)"); }
    catch { toast.error("Could not save"); }
    setBusy(false);
  };

  if (!loaded) return <p className="text-sm text-zinc-400">Loading…</p>;

  return (
    <div data-testid="guardian-emergency-card">
      <p className="text-xs text-zinc-500 mb-4 flex items-start gap-1.5"><HeartPulse className="w-4 h-4 text-rose-500 shrink-0" />
        Stored <b>encrypted</b>. Each section is revealed to your emergency contacts & guardians only under the rule you choose — and every viewing is logged.</p>
      <div className="space-y-4">
        {GROUPS.map(([gid, title, keys]) => (
          <div key={gid} className="border border-zinc-200 bg-white rounded-2xl p-4" data-testid={`guardian-card-group-${gid}`}>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h3 className="font-heading font-800 text-sm">{title}</h3>
              <select value={rules[gid] || "sos_only"} onChange={(e) => setRules({ ...rules, [gid]: e.target.value })}
                data-testid={`guardian-card-rule-${gid}`}
                className="ml-auto border border-zinc-300 rounded-lg px-2 py-1.5 text-xs bg-white">
                {DISCLOSURES.map(([v, l]) => <option key={v} value={v}>Visible: {l}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {keys.map(([k, label]) => (
                <input key={k} value={fields[gid]?.[k] || ""} onChange={(e) => setF(gid, k, e.target.value)}
                  data-testid={`guardian-card-${gid}-${k}`} placeholder={label}
                  className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm" />
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="guardian-card-save"
        className="mt-4 inline-flex items-center gap-1.5 bg-rose-600 text-white text-sm font-800 rounded-full px-6 py-2.5 hover:bg-rose-700 disabled:opacity-50">
        <Save className="w-4 h-4" /> Save encrypted card
      </button>
      <p className="mt-2 text-[11px] text-zinc-400">Full passport numbers and payment details should never be stored here.</p>
    </div>
  );
}
